import {createSelector} from '@reduxjs/toolkit';
import SunCalc from 'suncalc';

import {selectLanguage} from '../selectors';
import {
	selectSolarActivityLevelFactor, selectSolarBatterySize,
	selectSolarDimmProfileData, selectSolarGenerators,
	selectSolarLocation,
	selectSolarLuminarCountValue,
	selectSolarPVMaxInput1, selectSolarPVMaxInput2, selectSolarPVMaxInput3, selectSolarPVMaxInput4, selectSolarSelfSufficiency,
	selectSolarVersion, springDate, summerDate, winterDate
} from './solarSelectors';
import {sunlightPercentage} from '../util/sunlightPercentage';
import {convertTimeToDecimal} from '../util/convertTimeToDecimal';

function sunriseAndSunset(date, location, lang) {

	if (!location) return null;

	const sunTimes = SunCalc.getTimes(date, location.latitude, location.longitude);
	const sunriseTs = Date.parse(sunTimes.sunrise.toLocaleString('en-US', {
		dateStyle: 'medium',
		timeStyle: 'medium',
		timeZone: location.timezoneName,
		hour12: false
	}));
	const sunsetTs = Date.parse(sunTimes.sunset.toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'medium', timeZone: location.timezoneName, hour12: false}));
	const sunrise = new Date(sunriseTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	const sunset = new Date(sunsetTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	const sunriseDecimal = convertTimeToDecimal(sunrise);
	const sunsetDecimal = convertTimeToDecimal(sunset);
	const sunlight = (sunriseDecimal - sunsetDecimal) * 24;
	const nightLength = 24 - (sunsetDecimal - sunriseDecimal) * 24;

	return {
		sunrise: sunrise,
		sunset: sunset,
		sunlight: sunlight,
		nightLength: nightLength
	};
}

function solarDimmProfileActiveHours(date, dimmProfileData, location, activityLevelFactor, version, lang) {

	if (!location) return null;

	const sunTimes = SunCalc.getTimes(date, location.latitude, location.longitude);
	const sunriseTs = Date.parse(sunTimes.sunrise.toLocaleString('en-US', {
		dateStyle: 'medium',
		timeStyle: 'medium',
		timeZone: location.timezoneName,
		hour12: false
	}));
	const sunsetTs = Date.parse(sunTimes.sunset.toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'medium', timeZone: location.timezoneName, hour12: false}));
	const sunrise = new Date(sunriseTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	const sunset = new Date(sunsetTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	// get sunlight mask and shift sunlight array by 12 hours
	const sunlight = sunlightPercentage(sunrise, sunset);
	const shiftedSunlight = sunlight.slice(12).concat(sunlight.slice(0, 12));
	const x = ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
	const y = x.map((name, index) =>
		// calculate dimm profile based on selected options
		(
			(dimmProfileData[version][index].level * (1 - shiftedSunlight[index]) * (1 - activityLevelFactor) +
				dimmProfileData[version][index].activity * (1 - shiftedSunlight[index]) * activityLevelFactor)
		)
	);
	// add up all values in y
	let totalActiveHours = y.reduce((a, b) => a + b, 0);
	if (totalActiveHours <= 0) {
		totalActiveHours = 0.1;
	}
	return totalActiveHours;
}

export const selectSolarParameters = state => state.solar.data.project.parameters;

// a
export const selectSolarAutarkieTage = createSelector([selectSolarVersion, selectSolarSelfSufficiency],
	(version, selfSufficiency) => selfSufficiency[version]
);

// b Akku Kapa gesamt [Wh]
export const selectSolarAkkuKapa = createSelector([selectSolarBatterySize],
	(solarBatteryType) => (
		solarBatteryType === 'Type1' ? 312 : 624
	)
);


// c Akkuladung max [%]
export const selectSolarAkkuladungMax = createSelector([selectSolarVersion, selectSolarParameters],
	(version, parameters) => parameters[version].maxBatteryCharge
);

//d Start safety [%]
export const selectSolarStartSavety = createSelector([selectSolarVersion, selectSolarParameters],
	(version, parameters) => parameters[version].startSafety
);

//e deep discharge limit [%]
export const selectSolarDeepDischargeLimit = createSelector([selectSolarVersion, selectSolarParameters],
	(version, parameters) => parameters[version].deepDischargeLimit
);


//g Eigenverbrauch [W]
export const selectSolarEigenverbrauch = createSelector([selectSolarVersion, selectSolarLuminarCountValue, selectSolarParameters],
	(version, lumCount, parameters) =>
		parameters[version].selfConsumption[lumCount]
);

//Power Limit [W]
export const selectSolarPowerLimit = createSelector([selectSolarVersion, selectSolarParameters],
	(version, parameters) =>
		parameters[version].powerLimit
);

// h active hours
export const selectSolarDimmProfileActiveHours = createSelector(
	[selectSolarDimmProfileData, selectSolarLocation, selectSolarActivityLevelFactor, selectSolarLuminarCountValue, selectSolarVersion, selectLanguage],
	(dimmProfileData, location, activityLevel, luminarCount, version, lang) => (
		{
			winter: solarDimmProfileActiveHours(
				winterDate,
				dimmProfileData,
				location,
				activityLevel,
				version,
				lang),
			spring: solarDimmProfileActiveHours(
				springDate,
				dimmProfileData,
				location,
				activityLevel,
				version,
				lang),
			summer: solarDimmProfileActiveHours(
				summerDate,
				dimmProfileData,
				location,
				activityLevel,
				version,
				lang)
		}
	)
);

// i dimmlevel safety [%]
export const selectSolarDimmLevelSafety = createSelector([selectSolarVersion, selectSolarParameters],
		(version, parameters) => parameters[version].dimmSafety
);

// n Nachtlänge [h]
export const selectSolarNachtlaenge = createSelector([selectSolarLocation, selectLanguage],
	(location, lang) => (
		{
			winter: sunriseAndSunset(winterDate, location, lang),
			spring: sunriseAndSunset(springDate, location, lang),
			summer: sunriseAndSunset(summerDate, location, lang)
		}
	)
);

// f1 maximale Leistung unter Berücksichtigung der Autarkiezeit [W]
export const selectSolarMaximalMitAutarkie = createSelector(
	[
		selectSolarVersion,
		selectSolarAutarkieTage,
		selectSolarAkkuKapa,
		selectSolarAkkuladungMax,
		selectSolarStartSavety,
		selectSolarDeepDischargeLimit,
		selectSolarEigenverbrauch,
		selectSolarDimmProfileActiveHours,
		selectSolarDimmLevelSafety,
		selectSolarNachtlaenge,
		selectSolarLuminarCountValue,
		selectSolarPowerLimit,
	],
	(
		version,
		as,
		b,
		cs,
		d,
		e,
		g,
		hs,
		i,
		n,
		lumCount,
		limit) => {

		let f1 = 0;
		if (version === 'AetoPro') {
			f1 = {
				winter: calculateF1Pro(as.winter, b, cs.winter, d, e, g, hs.winter, i, lumCount, limit),
				spring: calculateF1Pro(as.spring, b, cs.spring, d, e, g, hs.spring, i, lumCount, limit),
				summer: calculateF1Pro(as.summer, b, cs.summer, d, e, g, hs.summer, i, lumCount, limit),
			};
		} else {
			f1 = {
				winter: calculateF1Basic(as.winter, b, cs.winter, d, e, g, hs.winter, i, n.winter.nightLength, lumCount, limit),
				spring: calculateF1Basic(as.spring, b, cs.spring, d, e, g, hs.spring, i, n.spring.nightLength, lumCount, limit),
				summer: calculateF1Basic(as.summer, b, cs.summer, d, e, g, hs.summer, i, n.summer.nightLength, lumCount, limit),
			};
		}

		return f1;
	}
);

function getMaxPv(pvIn, eigenverbrauch, activeHours, lumCount, limit) {
	return {
		winter: Math.min(limit, (0.5 * pvIn.winter - 24 * eigenverbrauch) / activeHours.winter) / lumCount,
		spring: Math.min(limit, (0.5 * pvIn.spring - 24 * eigenverbrauch) / activeHours.spring) / lumCount,
		summer: Math.min(limit, (0.5 * pvIn.summer - 24 * eigenverbrauch) / activeHours.summer) / lumCount,
	};
}

export const selectSolarMaximalPv1 = createSelector(
	[selectSolarEigenverbrauch, selectSolarDimmProfileActiveHours, selectSolarPVMaxInput1, selectSolarLuminarCountValue, selectSolarPowerLimit],
	(eigenverbrauch, activeHours, pvIn, lumCount, limit) =>
		getMaxPv(pvIn, eigenverbrauch, activeHours, lumCount, limit)
);
export const selectSolarMaximalPv2 = createSelector(
	[selectSolarEigenverbrauch, selectSolarDimmProfileActiveHours, selectSolarPVMaxInput2, selectSolarLuminarCountValue, selectSolarPowerLimit],
	(eigenverbrauch, activeHours, pvIn, lumCount, limit) =>
		getMaxPv(pvIn, eigenverbrauch, activeHours, lumCount, limit)
);
export const selectSolarMaximalPv3 = createSelector(
	[selectSolarEigenverbrauch, selectSolarDimmProfileActiveHours, selectSolarPVMaxInput3, selectSolarLuminarCountValue, selectSolarPowerLimit],
	(eigenverbrauch, activeHours, pvIn, lumCount, limit) =>
		getMaxPv(pvIn, eigenverbrauch, activeHours, lumCount, limit)
);
export const selectSolarMaximalPv4 = createSelector(
	[selectSolarEigenverbrauch, selectSolarDimmProfileActiveHours, selectSolarPVMaxInput4, selectSolarLuminarCountValue, selectSolarPowerLimit],
	(eigenverbrauch, activeHours, pvIn, lumCount, limit) =>
		getMaxPv(pvIn, eigenverbrauch, activeHours, lumCount, limit)
);
export const selectSolarMaximalPv = createSelector(
	[selectSolarMaximalPv1, selectSolarMaximalPv2, selectSolarMaximalPv3, selectSolarMaximalPv4, selectSolarGenerators],
	(maxPv1, maxPv2, maxPv3, maxPv4, generators) =>
		[maxPv1, maxPv2, maxPv3, maxPv4][generators - 1]
);
export const selectSolarMaximalPvAllGenerators = createSelector(
	[selectSolarMaximalPv1, selectSolarMaximalPv2, selectSolarMaximalPv3, selectSolarMaximalPv4],
	(maxPv1, maxPv2, maxPv3, maxPv4) =>
		[maxPv1, maxPv2, maxPv3, maxPv4]
);

export const selectSolarMaxPower = createSelector(
	[selectSolarMaximalMitAutarkie, selectSolarMaximalPv, selectSolarVersion],
	(maximalMitAutarkie, maximalPv, version) => getMaxSolarPower(maximalMitAutarkie, maximalPv, version)
);
export const selectSolarMaxPower1 = createSelector(
	[selectSolarMaximalMitAutarkie, selectSolarMaximalPv1, selectSolarVersion],
	(maximalMitAutarkie, maximalPv, version) => getMaxSolarPower(maximalMitAutarkie, maximalPv, version)
);
export const selectSolarMaxPower2 = createSelector(
	[selectSolarMaximalMitAutarkie, selectSolarMaximalPv2, selectSolarVersion],
	(maximalMitAutarkie, maximalPv, version) => getMaxSolarPower(maximalMitAutarkie, maximalPv, version)
);
export const selectSolarMaxPower3 = createSelector(
	[selectSolarMaximalMitAutarkie, selectSolarMaximalPv3, selectSolarVersion],
	(maximalMitAutarkie, maximalPv, version) => getMaxSolarPower(maximalMitAutarkie, maximalPv, version)
);
export const selectSolarMaxPower4 = createSelector(
	[selectSolarMaximalMitAutarkie, selectSolarMaximalPv4, selectSolarVersion],
	(maximalMitAutarkie, maximalPv, version) => getMaxSolarPower(maximalMitAutarkie, maximalPv, version)
);


export const selectTageVolleLeistung = createSelector(
	[selectSolarAkkuKapa, selectSolarAkkuladungMax, selectSolarStartSavety, selectSolarEigenverbrauch, selectSolarDimmProfileActiveHours, selectSolarMaxPower],
	(b, c, d, g, h, maxPow) => ({
		winter: calculateTageVolleLeistung(b, c.winter, d, g, h.winter, maxPow.winter),
		spring: calculateTageVolleLeistung(b, c.spring, d, g, h.spring, maxPow.spring),
		summer: calculateTageVolleLeistung(b, c.summer, d, g, h.summer, maxPow.summer),
	})
);
export const selectTageSafety = createSelector(
	[selectSolarAkkuKapa, selectSolarStartSavety, selectSolarDeepDischargeLimit, selectSolarEigenverbrauch, selectSolarDimmLevelSafety, selectSolarNachtlaenge, selectSolarMaxPower, selectSolarVersion],
	(b, d, e, g, i, n, maxPow, version) => ({
		winter: calculateTageSafetyModus(b, d, e, g, i, n.winter.nightLength, maxPow.winter, version),
		spring: calculateTageSafetyModus(b, d, e, g, i, n.spring.nightLength, maxPow.spring, version),
		summer: calculateTageSafetyModus(b, d, e, g, i, n.summer.nightLength, maxPow.winter, version),
	})
);


function calculateTageVolleLeistung(b, c, d, g, h, maxPow) {
	return b * (c - d) / (maxPow * h + 24 * g);
}

function calculateTageSafetyModus(b, d, e, g, i, n, maxPow, version) {
	// console.log(calculateTageSafetyModus, {b, d, e, g, i, n, maxPow, version});
	if (version === 'AetoPro') {
		// (C7−C8)×C5÷(C11×C17×C10+24×C9)
		return (d - e) * b / (i * maxPow + 24 * g);
	} else {
		// (D7−D8)×D5÷(D11×D17×D12+24×D9)
		return (d - e) * b / (i * maxPow * n + 24 * g);
	}
}

function calculateF1Pro(a, b, c, d, e, g, h, i, lumCount, limit) {
	//console.log('calculateF1Pro', a, b, c, d, e, g, h, i, lumCount);
	const u = a * (h * h) * i;
	const v = a * h * 24 * g + a * h * i * 24 * g - b * h * (i * (c - d) + (d - e));
	const w = a * 24 * 24 * (g * g) - b * 24 * g * ((c - d) + (d - e));
	const f1 = (-1 * v + Math.sqrt((v * v) - 4 * u * w)) / (2 * u);
	return Math.min(limit, f1 / lumCount);
}

function calculateF1Basic(a, b, c, d, e, g, h, i, n, lumCount, limit) {
	//console.log('calculateF1Basic', {a, b, c, d, e, g, h, i, n, lumCount});
	const x = a * h * i * n;
	const y = h * g * 24 * a + 24 * g * i * n * a - b * (c - d) * i * n - b * (d - e) * h;
	const z = (g * g) * (24 * 24) * a - g * 24 * (b * (c - d) + b * (d - e));
	const f1 = ((-1 * y) + Math.sqrt((y * y) - 4 * x * z)) / (2 * x);
	return Math.min(limit, f1 / lumCount);
}

function getMaxSolarPower(maximalMitAutarkie, maximalPv, version) {
	const winter = Math.min(maximalMitAutarkie.winter, maximalPv.winter);
	const spring = Math.min(maximalMitAutarkie.spring, maximalPv.spring);
	const summer = Math.min(maximalMitAutarkie.summer, maximalPv.summer);
	const min = Math.min(winter, spring, summer);

	if (maximalMitAutarkie && maximalPv) {
		if (version === 'AetoPro') {
			return {winter: winter, spring: spring, summer: summer};
		} else {
			// basic gets only minimal for all seasons
			return {winter: min, spring: min, summer: min};
		}
	} else {
		return {winter: 0, spring: 0, summer: 0};
	}
}
